/**
 * The soft-lock on the family being edited (screens §P6, ADR-0026 §9): claimed on entry, refreshed every 60 s, released on
 * leaving. Presence only; `takeOver` moves a lock a teammate holds.
 */
import { useCallback, useEffect } from "react";
import type { DraftView } from "@bsim/contracts";
import type { DecisionFamily } from "@bsim/domain";
import type { ApiClient } from "../api/client";

const REFRESH_MS = 60_000;

export function useFamilyLock(api: ApiClient, teamId: string, roundId: string, family: DecisionFamily | null, enabled: boolean, onDraft: (d: DraftView) => void) {
  const claim = useCallback(async (takeOver: boolean) => {
    if (family === null) return;
    const result = await api.call("claimFamilyLock", { teamId, roundId }, { family, takeOver });
    if (result.ok) onDraft(result.value);
  }, [api, teamId, roundId, family, onDraft]);

  useEffect(() => {
    if (family === null || !enabled) return;
    void claim(false);
    const refresh = setInterval(() => void claim(false), REFRESH_MS);
    return () => {
      clearInterval(refresh);
      void api.call("releaseFamilyLock", { teamId, roundId, family }, undefined);
    };
  }, [api, teamId, roundId, family, enabled, claim]);

  return { takeOver: () => claim(true) };
}
